<template>
    <div class="weeduc-admin-page corporate-page text-start p-0">
        <header class="weeduc-admin-page__header">
            <div class="weeduc-admin-page__header-lead">
                <TitlePage
                    :title="t.collaborators.editTitle"
                    :subtitle="t.collaborators.editSubtitle"
                    :hasMargin="false"
                />
            </div>
            <div class="weeduc-admin-page__header-actions">
                <v-btn
                    variant="tonal"
                    color="primary"
                    rounded="lg"
                    class="text-none"
                    @click="goBack"
                >
                    <v-icon class="me-1" size="small">mdi-arrow-left</v-icon>
                    {{ t.detail.back }}
                </v-btn>
            </div>
        </header>

        <div class="weeduc-admin-page__shell">
            <div class="weeduc-admin-page__panel">
                <div v-if="loading" class="corporate-placeholder">
                    <Loader :isActive="false" />
                </div>
                <div v-else-if="error" class="corporate-placeholder">{{ t.common.error }}</div>
                <template v-else>
                    <div class="corporate-dialog__body collaborator-edit__body">
                        <CollaboratorFormFields
                            v-model="form"
                            :labels="t.collaborators"
                            :sector-options="sectorOptions"
                            :role-options="roleOptions"
                            :roles-raw="rolesRaw"
                            :status-options="statusOptions"
                            :country-options="countryOptions"
                            :brazilian-states="brazilianStates"
                            :photo-file="photoFile"
                            :photo-preview="photoPreview"
                            :loading-address="loadingAddress"
                            @photo-change="onPhotoChange"
                            @photo-clear="clearPhoto"
                            @cep-change="getCep"
                            @sector-change="onSectorChange"
                        />
                    </div>
                    <div class="collaborator-edit__actions">
                        <v-btn
                            color="error"
                            variant="tonal"
                            rounded="lg"
                            class="text-none"
                            :disabled="saving || removing"
                            @click="removeOpen = true"
                        >
                            <v-icon class="me-1" size="small">mdi-delete-outline</v-icon>
                            {{ t.collaborators.delete }}
                        </v-btn>
                        <div class="collaborator-edit__actions-end">
                            <v-btn variant="text" class="text-none" @click="goBack">
                                {{ t.common.cancel }}
                            </v-btn>
                            <v-btn
                                color="primary"
                                variant="flat"
                                rounded="lg"
                                class="text-none"
                                :loading="saving"
                                :disabled="removing"
                                @click="save"
                            >
                                {{ t.collaborators.saveEdit }}
                            </v-btn>
                        </div>
                    </div>
                </template>
            </div>
        </div>

        <v-dialog v-model="saveErrorOpen" max-width="480">
            <v-card rounded="lg" class="corporate-dialog">
                <v-card-title class="corporate-dialog__header">
                    <h2 class="corporate-dialog__title">{{ errorDialogTitle }}</h2>
                </v-card-title>
                <v-card-text class="corporate-dialog__body">
                    <p class="corporate-dialog__hint mb-0">{{ saveErrorMessage }}</p>
                </v-card-text>
                <v-card-actions class="corporate-dialog__actions">
                    <v-spacer />
                    <v-btn
                        color="primary"
                        variant="flat"
                        rounded="lg"
                        class="text-none"
                        @click="saveErrorOpen = false"
                    >
                        {{ t.common.close }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="removeOpen" max-width="480" persistent>
            <v-card rounded="lg" class="corporate-dialog">
                <v-card-title class="corporate-dialog__header">
                    <h2 class="corporate-dialog__title">{{ t.collaborators.delete }}</h2>
                </v-card-title>
                <v-card-text class="corporate-dialog__body">
                    <p class="corporate-dialog__hint mb-0">{{ t.collaborators.deleteConfirm }}</p>
                    <p v-if="form.name" class="corporate-dialog__user-line mt-3 mb-0">
                        {{ form.name }}
                    </p>
                </v-card-text>
                <v-card-actions class="corporate-dialog__actions">
                    <v-spacer />
                    <v-btn
                        variant="text"
                        class="text-none"
                        :disabled="removing"
                        @click="closeRemove"
                    >
                        {{ t.common.cancel }}
                    </v-btn>
                    <v-btn
                        color="error"
                        variant="flat"
                        rounded="lg"
                        class="text-none"
                        :loading="removing"
                        @click="executeRemove"
                    >
                        {{ t.collaborators.delete }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import TitlePage from '@/components/Weflix/titlePage.vue';
import Loader from '@/components/Loader/loader.vue';
import CollaboratorFormFields from '@/components/Admin/Corporate/CollaboratorFormFields.vue';
import usersApi, {
    resolveCorporateUserType,
    canMarkCollaboratorResponsible,
} from '@/utils/corporate/users';
import organizationalUnitsApi from '@/utils/corporate/organizationalUnits';
import rolesApi from '@/utils/corporate/roles';
import { corporateI18n } from '@/i18n/mixins';
import brazilianStates from '@/mocks/brazilianStates';
import apiCep from '@/utils/cep';
import apiAWS from '@/utils/storage/aws';
import DefaultProfileImg from '@/assets/user.png';
import { mapState } from 'vuex';

function apiErrorMessage(error, fallback = 'Erro inesperado.') {
    const data = error?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object' && data.message) return data.message;
    if (error?.message) return error.message;
    return fallback;
}

function digitsOnly(value) {
    return String(value || '').replace(/\D/g, '');
}

function formatCpf(value) {
    const digits = digitsOnly(value).slice(0, 11);
    return digits
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

function formatPhone(value) {
    const digits = digitsOnly(value).slice(0, 11);
    if (digits.length <= 10) {
        return digits
            .replace(/(\d{2})(\d)/, '($1) $2')
            .replace(/(\d{4})(\d)/, '$1-$2');
    }
    return digits
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d)/, '$1-$2');
}

function formatCep(value) {
    const digits = digitsOnly(value).slice(0, 8);
    return digits.replace(/(\d{5})(\d)/, '$1-$2');
}

export default {
    name: 'CorporateCollaboratorEdit',
    mixins: [corporateI18n],
    components: {
        TitlePage,
        Loader,
        CollaboratorFormFields,
    },
    data() {
        return {
            loading: true,
            error: false,
            saving: false,
            saveErrorOpen: false,
            saveErrorMessage: '',
            errorDialogTitle: '',
            removeOpen: false,
            removing: false,
            loadingAddress: false,
            photoFile: null,
            photoPreviewUrl: '',
            brazilianStates,
            sectorsRaw: [],
            rolesRaw: [],
            sectorOptions: [],
            roleOptions: [],
            form: {
                name: '',
                email: '',
                photo: '',
                socialNumber: '',
                idNumber: '',
                phone: '',
                postcode: '',
                address: '',
                addressNumber: '',
                addressComplement: '',
                neighborhood: '',
                city: '',
                county: 'AC',
                country: 'br',
                sectorId: null,
                roleId: null,
                managerUserId: null,
                admissionDate: null,
                internalCode: '',
                isResponsible: false,
                status: 'active',
                type: 3,
            },
        };
    },
    computed: {
        ...mapState(['token']),
        userId() {
            return this.$route.params.id;
        },
        statusOptions() {
            return [
                { text: this.t.collaborators.active, value: 'active' },
                { text: this.t.collaborators.inactive, value: 'inactive' },
            ];
        },
        countryOptions() {
            return [
                { text: 'Brasil', value: 'br' },
                { text: 'Outro', value: 'outro' },
            ];
        },
        photoPreview() {
            if (this.photoPreviewUrl) return this.photoPreviewUrl;
            if (typeof this.form.photo === 'string' && this.form.photo) {
                return this.form.photo;
            }
            return DefaultProfileImg;
        },
    },
    async created() {
        await this.loadLookups();
        await this.loadUser();
    },
    beforeUnmount() {
        this.clearPhotoPreviewUrl();
    },
    methods: {
        goBack() {
            this.$router.push(`/dashboard/corporativo/colaboradores/${this.userId}`);
        },
        applyRoleOptions(roles = []) {
            this.rolesRaw = roles;
            this.roleOptions = roles.map((r) => ({ text: r.name, value: r.id }));
        },
        ensureOption(options, value, text) {
            if (!value) return options;
            if (options.some((o) => o.value === value)) return options;
            return [{ text: text || String(value), value }, ...options];
        },
        async loadSectors() {
            // GET /corporate/organizational-units?type=sector
            try {
                const sectors = await organizationalUnitsApi.listSectors(this.token);
                this.sectorsRaw = sectors.content || [];
                this.sectorOptions = (sectors.content || []).map((s) => ({
                    text: s.name,
                    value: s.id,
                }));
                return sectors;
            } catch (e) {
                console.warn('[CollaboratorEdit] falha ao listar setores', e);
                // Retry sem filtro de status (alguns backends rejeitam status=active)
                try {
                    const sectors = await organizationalUnitsApi.list(this.token, { type: 'sector' });
                    this.sectorsRaw = sectors.content || [];
                    this.sectorOptions = (sectors.content || []).map((s) => ({
                        text: s.name,
                        value: s.id,
                    }));
                    return sectors;
                } catch (e2) {
                    console.error('[CollaboratorEdit] setores indisponíveis', e2);
                    this.sectorsRaw = [];
                    this.sectorOptions = [];
                    return { content: [], managers: [] };
                }
            }
        },
        async loadRoles(sectorId) {
            // GET /corporate/roles (?sectorId=)
            try {
                const res = await rolesApi.list(this.token, {
                    sectorId: sectorId || undefined,
                });
                this.applyRoleOptions(res.content || []);
                return res;
            } catch (e) {
                console.error('[CollaboratorEdit] falha ao listar cargos', e);
                this.applyRoleOptions([]);
                return { content: [] };
            }
        },
        async loadLookups() {
            await Promise.all([
                this.loadSectors(),
                this.loadRoles(),
            ]);
        },
        async onSectorChange(sectorId) {
            await this.loadRoles(sectorId || undefined);
            if (!sectorId) return;
            // Se o cargo atual sumiu da lista filtrada, mantém opção legível
            if (
                this.form.roleId
                && !this.roleOptions.some((o) => o.value === this.form.roleId)
            ) {
                this.roleOptions = this.ensureOption(
                    this.roleOptions,
                    this.form.roleId,
                    'Cargo atual',
                );
            }
        },
        async loadUser() {
            this.loading = true;
            this.error = false;
            try {
                const user = await usersApi.getById(this.token, this.userId);
                this.form = {
                    name: user.name || '',
                    email: user.email || '',
                    photo: user.photo || '',
                    socialNumber: formatCpf(user.socialNumber),
                    idNumber: user.idNumber || '',
                    phone: formatPhone(user.phone),
                    postcode: formatCep(user.postcode),
                    address: user.address || '',
                    addressNumber: user.addressNumber || '',
                    addressComplement: user.addressComplement || '',
                    neighborhood: user.neighborhood || '',
                    city: user.city || '',
                    county: user.county || 'AC',
                    country: user.country || 'br',
                    sectorId: user.sectorId || null,
                    roleId: user.roleId || null,
                    managerUserId: user.managerUserId || null,
                    admissionDate: user.admissionDate || null,
                    internalCode: user.internalCode || '',
                    isResponsible: Boolean(user.isResponsible),
                    status: user.status || 'active',
                    type: resolveCorporateUserType(user.type),
                };

                // Garante opções visíveis mesmo se a listagem falhar/atrasar
                this.sectorOptions = this.ensureOption(
                    this.sectorOptions,
                    user.sectorId,
                    user.sectorName || 'Setor atual',
                );
                this.roleOptions = this.ensureOption(
                    this.roleOptions,
                    user.roleId,
                    user.roleName || 'Cargo atual',
                );

                if (user.sectorId) {
                    await this.loadRoles(user.sectorId);
                    this.roleOptions = this.ensureOption(
                        this.roleOptions,
                        user.roleId,
                        user.roleName || 'Cargo atual',
                    );
                }
            } catch (e) {
                console.error(e);
                this.error = true;
            } finally {
                this.loading = false;
            }
        },
        clearPhotoPreviewUrl() {
            if (this.photoPreviewUrl) {
                URL.revokeObjectURL(this.photoPreviewUrl);
                this.photoPreviewUrl = '';
            }
        },
        onPhotoChange(value) {
            const file = Array.isArray(value) ? value[0] : value;
            this.clearPhotoPreviewUrl();
            this.photoFile = file instanceof File ? file : null;
            if (file instanceof File) {
                this.photoPreviewUrl = URL.createObjectURL(file);
                this.form = { ...this.form, photo: file };
            } else {
                this.form = { ...this.form, photo: '' };
            }
        },
        clearPhoto() {
            this.clearPhotoPreviewUrl();
            this.photoFile = null;
            this.form = { ...this.form, photo: '' };
        },
        async getCep() {
            if (!this.form.postcode) return;
            this.loadingAddress = true;
            try {
                const response = await apiCep.getDataCep(this.form.postcode);
                this.form = {
                    ...this.form,
                    address: response.logradouro || this.form.address,
                    neighborhood: response.bairro || this.form.neighborhood,
                    city: response.localidade || this.form.city,
                    county: response.uf || this.form.county,
                };
            } catch (e) {
                alert('Erro ao buscar o CEP. Verifique o valor ou preencha o endereço manualmente.');
            } finally {
                this.loadingAddress = false;
            }
        },
        async resolvePhoto() {
            const photo = this.form.photo;
            if (typeof photo === 'string') {
                if (photo.startsWith('data:')) {
                    throw new Error('A foto deve ser enviada ao bucket, não em base64.');
                }
                return photo;
            }
            if (!(photo instanceof File)) return '';

            const signature = await apiAWS.getSignatureImage(this.token, photo.name);
            if (!signature?.url || !signature?.token) {
                throw new Error('Não foi possível obter URL assinada para a foto.');
            }
            await apiAWS.putFileAWS(signature.url, photo);
            return signature.token;
        },
        async save() {
            if (!this.form.name?.trim() || !this.form.email?.trim()) return;
            this.saving = true;
            try {
                const photo = await this.resolvePhoto();
                await usersApi.update(this.token, this.userId, {
                    name: this.form.name,
                    email: this.form.email,
                    phone: this.form.phone,
                    photo,
                    status: this.form.status,
                    sectorId: this.form.sectorId,
                    roleId: this.form.roleId,
                    managerUserId: this.form.managerUserId,
                    admissionDate: this.form.admissionDate || null,
                    internalCode: this.form.internalCode || '',
                    isResponsible: canMarkCollaboratorResponsible(this.form)
                        && Boolean(this.form.isResponsible),
                    type: resolveCorporateUserType(this.form.type),
                    // Campos extras usados no mock / se o backend aceitar
                    socialNumber: this.form.socialNumber,
                    idNumber: this.form.idNumber,
                    postcode: this.form.postcode,
                    address: this.form.address,
                    addressNumber: this.form.addressNumber,
                    addressComplement: this.form.addressComplement,
                    neighborhood: this.form.neighborhood,
                    city: this.form.city,
                    county: this.form.county,
                    country: this.form.country,
                });
                this.$router.push(`/dashboard/corporativo/colaboradores/${this.userId}`);
            } catch (e) {
                this.showError(this.t.collaborators.saveErrorTitle, apiErrorMessage(e, this.t.common.error));
            } finally {
                this.saving = false;
            }
        },
        closeRemove() {
            if (this.removing) return;
            this.removeOpen = false;
        },
        showError(title, message) {
            this.errorDialogTitle = title;
            this.saveErrorMessage = message;
            this.saveErrorOpen = true;
        },
        async executeRemove() {
            this.removing = true;
            try {
                await usersApi.remove(this.token, this.userId);
                this.removeOpen = false;
                this.$router.push('/dashboard/corporativo/colaboradores');
            } catch (e) {
                this.removeOpen = false;
                this.showError(
                    this.t.collaborators.deleteErrorTitle,
                    apiErrorMessage(e, this.t.common.error),
                );
            } finally {
                this.removing = false;
            }
        },
    },
};
</script>

<style scoped>
@import '@/css/weeduc/admin/corporate.css';

.collaborator-edit__body {
    padding: 0;
}

.collaborator-edit__actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding-top: 1.25rem;
    margin-top: 0.5rem;
    border-top: 1px solid #e8eaf0;
}

.collaborator-edit__actions-end {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
}
</style>
